import { useState, type CSSProperties, type ReactNode } from 'react';

/** A conic-gradient readiness ring. `value` is 0-100. */
export function Ring({
  value,
  label,
  sm,
}: {
  value: number;
  label: string;
  sm?: boolean;
}) {
  return (
    <div
      className={`ring${sm ? ' sm' : ''}`}
      style={{ '--v': value } as CSSProperties}
    >
      <b>{value}%</b>
      <small>{label}</small>
    </div>
  );
}

export type StepStatus = 'done' | 'active' | 'locked';

export function Stepper({
  nodes,
  onOpen,
}: {
  nodes: {
    id: string;
    num: string;
    name: string;
    tag: string;
    status: StepStatus;
    prog: string;
  }[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="stepper">
      {nodes.map((n) => (
        <button
          key={n.id}
          type="button"
          className={`snode ${n.status === 'locked' ? 'todo' : n.status}`}
          onClick={() => onOpen(n.id)}
        >
          <span className="sdot">{n.status === 'done' ? '✓' : n.num}</span>
          <span className="sname">{n.name}</span>
          <span className="sdesc">{n.tag}</span>
          <span className="sprog">{n.prog}</span>
        </button>
      ))}
    </div>
  );
}

export interface TileSpec {
  k: string;
  v: ReactNode;
  delta?: string;
  deltaClass?: string;
  rag?: 'ok' | 'warn' | 'bad';
}

export function KpiTiles({ tiles }: { tiles: TileSpec[] }) {
  return (
    <div className="tiles">
      {tiles.map((t) => (
        <div className="tile" key={t.k}>
          {t.rag && <span className={`rag-dot ${t.rag}`} />}
          <div className="k">{t.k}</div>
          <div className="v">{t.v}</div>
          {t.delta && (
            <div className={`delta ${t.deltaClass ?? 'flat'}`}>{t.delta}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export function Gauges({
  items,
}: {
  items: { id: string; pct: number; sub: string; color: string }[];
}) {
  return (
    <div className="gauges">
      {items.map((g) => (
        <div className="gauge" key={g.id}>
          <div
            className="gring"
            style={{ '--v': g.pct, '--c': g.color } as CSSProperties}
          >
            <b>{g.pct}%</b>
          </div>
          <div className="gl">{g.id}</div>
          <div className="gs">{g.sub}</div>
        </div>
      ))}
    </div>
  );
}

function ActionRow({ t, m, phase }: { t: string; m: string; phase: string }) {
  const [on, setOn] = useState(false);
  return (
    <div className={`action${on ? ' on' : ''}`}>
      <button
        type="button"
        className="achk"
        onClick={() => setOn((v) => !v)}
        aria-label="Mark done"
      >
        ✓
      </button>
      <div className="abody">
        <div className="at">{t}</div>
        <div className="am">{m}</div>
      </div>
      <span className="aphase">{phase}</span>
    </div>
  );
}

export function NextActions({
  items,
}: {
  items: { t: string; m: string; phase: string }[];
}) {
  return (
    <div className="actions">
      {items.map((a, i) => (
        <ActionRow key={i} {...a} />
      ))}
    </div>
  );
}
